import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import {
    FeatureFlag,
    FeatureFlagService,
} from 'app/core/services/feature-flag.service';

/**
 * Factory for a route guard that keeps a feature behind its Unleash flag
 * (mvp-release-scope-2026-10-13 §5.2: nav hides the item, this guard blocks
 * the direct URL). Flag OFF → `/errors/not-found`, as if the page did not
 * exist on this environment.
 *
 * Waits (bounded) for the first Unleash answer so a hard load of a gated URL
 * is decided by Unleash, not just the bootstrap; when Unleash is unreachable
 * the `FEATURE_PFL_*` bootstrap decides.
 *
 * Works as `canActivate` or `canMatch`. Use `canMatch` on a component shell
 * whose children must not start activating before the flags answer
 * (marketplace, kb#2890): an unmatched route never emits ActivationStart.
 *
 * @example
 * { path: 'billing', canActivate: [featureFlagGuard(FEATURE_FLAGS.BILLING)], ... }
 */
export function featureFlagGuard(
    flag: FeatureFlag
): CanActivateFn & CanMatchFn {
    return async () => {
        const flags = inject(FeatureFlagService);
        const router = inject(Router);
        await flags.whenReady();
        return (
            flags.isEnabled(flag) || router.createUrlTree(['/errors/not-found'])
        );
    };
}
